import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    inject,
    input,
    InputSignal,
    OnDestroy,
    output,
    Signal,
    signal,
    WritableSignal,
    effect,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { DatiqTemplateModule } from '@dat/datiq-components-pkg/template';
import { IqEventBusBrokerHandler, IqEventBusBrokerService } from '@dat/iq-event-bus';
import { get, isEmpty, isEqual, isNumber, round } from 'lodash';
import { z } from 'zod';
import { McGaugeMciStepPipe } from '../mc-gauge-mci-step/mc-gauge-mci-step.pipe';
import { SearchInfoMci } from '../models';

const MciGaugeQueryPayloadSchema = z.object({
    id: z.string(),
    score: z.number(),
    loaded: z.boolean().default(false),
    hasError: z.boolean().default(false),
    noData: z.boolean().default(false),
    capacity: z.string().nullable(),
    capacityMap: z
        .record(
            z.string(),
            z.object({
                high: z.number(),
                low: z.number(),
                color: z.string().nullable(),
                index: z.number(),
            }),
        )
        .default({}),
});

export type MciGaugeQueryPayload = z.infer<typeof MciGaugeQueryPayloadSchema>;

@Component({
    selector: 'iq-mc-gauge-query-mci',
    standalone: true,
    imports: [
        CommonModule,
        DatiqTemplateModule,
        McGaugeMciStepPipe,
        MatIconModule,
    ],
    templateUrl: './iq-mc-gauge-query-mci.component.html',
    styleUrl: './iq-mc-gauge-query-mci.component.scss',
})
export class IqMcGaugeQueryMciComponent implements OnDestroy {
    id: InputSignal<string> = input('');
    data: InputSignal<MciGaugeQueryPayload> = input();
    onClick = output();

    private _eventBus: IqEventBusBrokerService = inject(
        IqEventBusBrokerService,
    );

    loadedMci: Signal<boolean> = computed(() => {
        return this._payload().loaded;
    }, {
        equal: isEqual
    });
    errorMci: Signal<boolean> = computed(() => {
        return this._payload().hasError;
    }, {
        equal: isEqual
    });
    noDataMci: Signal<boolean> = computed(() => {
        return this._payload().noData;
    }, {
        equal: isEqual
    });
    score: WritableSignal<number> = signal(0);
    dataInfoMci: Signal<string> = computed(() => {
        const loaded = this.loadedMci();
        const noData = this.noDataMci();
        const hasError = this.errorMci();

        if (!loaded) return '--';
        if (noData) return '**-- MCI**';
        if (hasError) return '**Error loading MCI Data.**';

        const score = this._payload().score;
        const capacity = this._payload().capacity;

        return `**${score} MCI** ${capacity}`;
    });

    currentSearchMci: WritableSignal<SearchInfoMci> = signal({});

    lowLabel: Signal<string> = computed(() => {
        return '-100';
    }, {
        equal: isEqual
    });
    highLabel: Signal<string> = computed(() => {
        return '+100';
    }, {
        equal: isEqual
    });

    steps: Signal<
        { color?: string; index?: number; high?: number; low?: number }[]
    > = computed(() => {
        const capacityMap = this._payload().capacityMap;
        return isEmpty(capacityMap)
            ? this.emptySteps()
            : Object.values(capacityMap);
    }, {
        equal: isEqual
    });

    scenario: Signal<'weighted' | 'unweighted'> = computed(() => {
        return 'unweighted';
    }, {
        equal: isEqual
    });

    currentEscalation: WritableSignal<string> = signal('');
    gaugeMarkerPosition: Signal<number> = computed(() => {
        const score = this._payload().score;

        if (!isNumber(score)) return 0;

        const capacityMap = this._payload().capacityMap;
        const capacity = this._payload().capacity;
        const currentEquipmentRangeScore = get(capacityMap, [capacity], {
            high: 0,
            low: 0,
        });
        const offsetScore = score - currentEquipmentRangeScore.low;
        const offsetHigh =
            currentEquipmentRangeScore.high - currentEquipmentRangeScore.low;
        const percentPosition = round((offsetScore / offsetHigh) * 100);

        return percentPosition;
    }, {
        equal: isEqual
    });

    gaugeMarkerPositionIndex: Signal<number> = computed(() => {
        const capacityMap = this._payload().capacityMap;
        const capacity = this._payload().capacity;
        const index = get(capacityMap, [capacity, 'index'], 0);

        return index;
    }, {
        equal: isEqual
    });

    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    private _deregisterMap: Record<string, IqEventBusBrokerHandler> = {};
    private _payload: WritableSignal<MciGaugeQueryPayload> = signal({
        id: '',
        score: 0,
        loaded: false,
        hasError: false,
        noData: false,
        capacity: '',
        capacityMap: {},
    });

    parseUpdateMciQueryGaugePayload(payload: MciGaugeQueryPayload): any {
        const test: any = MciGaugeQueryPayloadSchema.safeParse(payload);

        if (!test.success) {
            console.warn('MciGaugeQueryPayloadSchema Invalid Payload');
            console.log(test?.error?.issues);
            return;
        }

        return this._payload.set(payload);
    }

    constructor() {
        effect(async () => {
            const newData = await this.data();

            if (newData?.score) {
                this.parseUpdateMciQueryGaugePayload(newData);
            }
        }, { allowSignalWrites: true });

        this._deregisterMap = this._bus.registerMap({
            updateMciQueryGauge: {
                callback: (payload: MciGaugeQueryPayload) => {
                    if (payload.id !== this.id()) return;

                    const test: any =
                        MciGaugeQueryPayloadSchema.safeParse(payload);

                    if (!test.success) {
                        console.warn(
                            'MciGaugeQueryPayloadSchema Invalid Payload',
                        );
                        console.log(test?.error?.issues);
                        return;
                    }

                    this._payload.set(payload);

                    this._bus.dispatch({
                        name: 'type:tracking:log',
                        payload: {
                            type: 'SEGMENT',
                            eventType: 'TRACK',
                            eventName: 'RateView MCI Gauge Displayed',
                            interaction: 'load',
                        },
                    });

                    if (this.loadedMci()) {
                        this._bus.dispatch({
                            name: 'rateviewLoadedMCIGauge',
                            payload: {
                                id: this.id()
                            }
                        })
                    }
                },
            },
        });
    }

    ngOnDestroy(): void {
        this._bus.unregisterMap(this._deregisterMap);
    }

    private emptySteps(): {
        color: string;
        index: number;
        high: number;
        low: number;
    }[] {
        return [1, 2, 3, 4, 5, 6].map((_: never, i) => ({
            color: 'white',
            index: i,
            high: 0,
            low: 0,
        }));
    }

    async click(): Promise<void> {
        const loaded: boolean = await this.loadedMci();
        /**
         * This dispatches an event when the gauge is clicked
         *
         *
         * To listen to these events, you can use the following format in your Dashboard, Section, or Component:
         *
         * ```json
         * {
         *  "onClick": [
         *    {
         *      "name": "EVENT_NAME",
         *      "payload": {},
         *      "sourceEventRule": {
         *          "payload": [
         *            {
         *                "path": "id",
         *                "operator": "eq",
         *                "value": "COMPONENT_ID"
         *            },
         *            {
         *                "path": "componentName",
         *                "operator": "eq",
         *                "value": "IqMcGaugeQueryMci"
         *            },
         *          ]
         *      },
         *      "rules": []
         *    },
         *   ],
         * }
         * ```
         */
        const clickDispatch = () =>
            this._eventBus.dispatch({
                name: 'type:click',
                payload: {
                    id: this.id(),
                    componentName: 'IqMcGaugeQueryMci',
                    mci: {
                        ...this.currentSearchMci(),
                    },
                },
            });

        if (!loaded) return;

        clickDispatch();
        this.onClick.emit();
    }

    // updateCache(mciInfo: SearchInfoMci) {
    //     const data =
    //         this.id() === 'rv-gauge-mc-origin'
    //             ? {
    //                   geoType: GeographyTypes.MarketArea,
    //                   originGeoCode: mciInfo.escalationQuery,
    //                   direction: this.direction(),
    //               }
    //             : this.id() === 'rv-gauge-mci-destination'
    //             ? {
    //                   geoType: GeographyTypes.MarketArea,
    //                   destinationGeoCode: mciInfo.escalationQuery,
    //                   direction: this.direction(),
    //               }
    //             : undefined;
    //     const actions: IDatiqRulesData<IDatiqAction>[] =
    //         createActionListNoRules([
    //             {
    //                 name: 'updateDashboardCache',
    //                 payload: {
    //                     id: 'rv-sl-results',
    //                     data: data,
    //                 },
    //             },
    //         ]);
    //     this._actions.processActions(actions, data);
    // }
}
